import { LanguageCodes, TEIComplexity, TemplateID } from '../../shared/enums';
import { Population } from '../../shared/interfaces';
import {
  learningTargets,
  TEST_LEARNING_TARGET_TYPES,
} from '../learning-targets';
import { TEST_HINT_TYPES, chartHints, hints } from '../hints';
import { TEST_IMAGE_TYPES, images } from '../images';
import { TEST_TEXTS_TYPES, texts } from '../texts';
import { TEST_CHOICE_TYPES } from '../choice-types';
import { TestAssociatedSuccessCriteria } from '../test-associated-success-criteria.enum';
import { multiChoiceQuestions } from '../multi-choice-questions';
import { multiChoiceImageQuestions } from '../multi-choice-image-questions';
import { multiSelectQuestions } from '../multi-select-questions';
import { multiSelectImageQuestions } from '../multi-select-image-questions';
import { inlineChoiceQuestions } from '../inline-choice-questions';
import { textEntryQuestions } from '../text-entry-questions';
import { constructedResponseQuestions } from '../constructed-response-questions';
import { extendedConstructedResponseQuestions } from '../extended-constructed-response-questions';
import { TEST_POPULATION_TYPES, copyHints } from '.';

/*****************************************************************/
/* SPANISH POPULATIONS                                           */
/*****************************************************************/

const constSpanishPopParams = {
  product: 'Lectura',
  language: LanguageCodes.SPANISH,
};

const constSpanishPopParamsMC = {
  templateID: TemplateID.MULTIPLE_CHOICE,
  complexity: 'Application' as TEIComplexity,
  associatedSuccessCriteria: ['multichoice'],
  domain: { text: 'Multichoice domain' },
  standard: { text: 'Multichoice standard' },
  learningTarget: [learningTargets[TEST_LEARNING_TARGET_TYPES.MULTI_CHOICE]],
  feedbackFirstIncorrect: {
    content: 'Insert first incorrect feedback - Test.R.MC',
  },
  feedbackSecondIncorrect: {
    content: 'Insert second incorrect feedback - Test.R.MC',
  },
  ...constSpanishPopParams,
};

export const populationsSpanishMC: { [key: number]: Population } = {
  [TEST_POPULATION_TYPES.TEST_S_MC_SHORT]: {
    id: `${TEST_POPULATION_TYPES.TEST_S_MC_SHORT}`,
    hint: copyHints(hints[TEST_HINT_TYPES.ES_SINGLE_SHORT]),
    populationData: multiChoiceQuestions[TEST_CHOICE_TYPES.TEST_S_MC_SHORT],
    ...constSpanishPopParamsMC,
  },
  [TEST_POPULATION_TYPES.TEST_S_MC_MEDIUM]: {
    id: `${TEST_POPULATION_TYPES.TEST_S_MC_MEDIUM}`,
    hint: copyHints(hints[TEST_HINT_TYPES.ES_SINGLE_MEDIUM]),
    populationData: multiChoiceQuestions[TEST_CHOICE_TYPES.TEST_S_MC_MEDIUM],
    ...constSpanishPopParamsMC,
  },
  [TEST_POPULATION_TYPES.TEST_S_MC_LONG]: {
    id: `${TEST_POPULATION_TYPES.TEST_S_MC_LONG}`,
    hint: copyHints(hints[TEST_HINT_TYPES.ES_SINGLE_LONG]),
    populationData: multiChoiceQuestions[TEST_CHOICE_TYPES.TEST_S_MC_LONG],
    ...constSpanishPopParamsMC,
  },
  [TEST_POPULATION_TYPES.TEST_S_MC_PASSAGE_SHORT]: {
    id: `${TEST_POPULATION_TYPES.TEST_S_MC_PASSAGE_SHORT}`,
    populationData: multiChoiceQuestions[TEST_CHOICE_TYPES.TEST_S_MC_SHORT],
    hint: copyHints(hints[TEST_HINT_TYPES.ES_DOUBLE_MEDIUM]),
    passage: texts[TEST_TEXTS_TYPES.SHORT],
    ...constSpanishPopParamsMC,
  },
  [TEST_POPULATION_TYPES.TEST_S_MC_PASSAGE_MEDIUM]: {
    id: `${TEST_POPULATION_TYPES.TEST_S_MC_PASSAGE_MEDIUM}`,
    populationData: multiChoiceQuestions[TEST_CHOICE_TYPES.TEST_S_MC_MEDIUM],
    hint: copyHints(hints[TEST_HINT_TYPES.ES_MULTIPLE_MEDIUM]),
    passage: texts[TEST_TEXTS_TYPES.MEDIUM],
    ...constSpanishPopParamsMC,
  },
  [TEST_POPULATION_TYPES.TEST_S_MC_PASSAGE_LONG]: {
    id: `${TEST_POPULATION_TYPES.TEST_S_MC_PASSAGE_LONG}`,
    instructions: {
      text: 'This passage has instructions about something that you should read about here.',
    },
    populationData: multiChoiceQuestions[TEST_CHOICE_TYPES.TEST_S_MC_LONG],
    hint: copyHints(hints[TEST_HINT_TYPES.ES_MULTIPLE_MEDIUM]),
    image: images[TEST_IMAGE_TYPES.CAT_UNDER_TABLE],
    passage: texts[TEST_TEXTS_TYPES.LONG],
    ...constSpanishPopParamsMC,
  },
  [TEST_POPULATION_TYPES.TEST_S_MC_TRUE_FALSE]: {
    id: `${TEST_POPULATION_TYPES.TEST_S_MC_TRUE_FALSE}`,
    hint: copyHints(hints[TEST_HINT_TYPES.ES_MULTIPLE_MEDIUM]),
    instructions: {
      text: 'This is instruction text for the true or false question type.',
    },
    populationData:
      multiChoiceQuestions[TEST_CHOICE_TYPES.TEST_S_MC_TRUE_FALSE],
    ...constSpanishPopParamsMC,
    templateID: TemplateID.TRUE_FALSE_CHOICE,
  },
  [TEST_POPULATION_TYPES.TEST_S_MC_PASSAGE_TRUE_FALSE]: {
    id: `${TEST_POPULATION_TYPES.TEST_S_MC_PASSAGE_TRUE_FALSE}`,
    hint: copyHints(hints[TEST_HINT_TYPES.ES_MULTIPLE_MEDIUM]),
    passage: texts[TEST_TEXTS_TYPES.LONG],
    image: images[TEST_IMAGE_TYPES.CAT_UNDER_TABLE],
    instructions: {
      text: 'This is instruction text for the true or false question type.',
    },
    populationData:
      multiChoiceQuestions[TEST_CHOICE_TYPES.TEST_S_MC_TRUE_FALSE],
    ...constSpanishPopParamsMC,
    templateID: TemplateID.TRUE_FALSE_CHOICE,
  },
};

const constSpanishPopParamsMCI = {
  templateID: TemplateID.MULTIPLE_CHOICE_IMAGE,
  complexity: 'Application' as TEIComplexity,
  associatedSuccessCriteria: ['multichoice-image'],
  domain: { text: 'Multichoice image domain' },
  standard: { text: 'Multichoice image standard' },
  learningTarget: [learningTargets[TEST_LEARNING_TARGET_TYPES.MULTI_CHOICE]],
  feedbackFirstIncorrect: {
    content: 'Insert first incorrect feedback - Test.R.MCI',
  },
  feedbackSecondIncorrect: {
    content: 'Insert second incorrect feedback - Test.R.MCI',
  },
  ...constSpanishPopParams,
};

export const populationsSpanishMCI: { [key: number]: Population } = {
  [TEST_POPULATION_TYPES.TEST_S_MCI_SHORT]: {
    id: `${TEST_POPULATION_TYPES.TEST_S_MCI_SHORT}`,
    populationData:
      multiChoiceImageQuestions[TEST_CHOICE_TYPES.TEST_S_MCI_SHORT],
    hint: copyHints(hints[TEST_HINT_TYPES.ES_SINGLE_SHORT]),
    ...constSpanishPopParamsMCI,
  },
  [TEST_POPULATION_TYPES.TEST_S_MCI_MEDIUM]: {
    id: `${TEST_POPULATION_TYPES.TEST_S_MCI_MEDIUM}`,
    populationData:
      multiChoiceImageQuestions[TEST_CHOICE_TYPES.TEST_S_MCI_MEDIUM],
    hint: copyHints(hints[TEST_HINT_TYPES.ES_SINGLE_MEDIUM]),
    ...constSpanishPopParamsMCI,
  },
  [TEST_POPULATION_TYPES.TEST_S_MCI_LONG]: {
    id: `${TEST_POPULATION_TYPES.TEST_S_MCI_LONG}`,
    populationData:
      multiChoiceImageQuestions[TEST_CHOICE_TYPES.TEST_S_MCI_LONG],
    hint: copyHints(hints[TEST_HINT_TYPES.ES_SINGLE_LONG]),
    ...constSpanishPopParamsMCI,
  },
  [TEST_POPULATION_TYPES.TEST_S_MCI_PASSAGE_SHORT]: {
    id: `${TEST_POPULATION_TYPES.TEST_S_MCI_PASSAGE_SHORT}`,
    populationData:
      multiChoiceImageQuestions[TEST_CHOICE_TYPES.TEST_S_MCI_SHORT],
    hint: copyHints(hints[TEST_HINT_TYPES.ES_DOUBLE_MEDIUM]),
    passage: texts[TEST_TEXTS_TYPES.SHORT],
    ...constSpanishPopParamsMCI,
  },
  [TEST_POPULATION_TYPES.TEST_S_MCI_PASSAGE_MEDIUM]: {
    id: `${TEST_POPULATION_TYPES.TEST_S_MCI_PASSAGE_MEDIUM}`,
    populationData:
      multiChoiceImageQuestions[TEST_CHOICE_TYPES.TEST_S_MCI_MEDIUM],
    hint: copyHints(hints[TEST_HINT_TYPES.ES_MULTIPLE_MEDIUM]),
    passage: texts[TEST_TEXTS_TYPES.MEDIUM],
    ...constSpanishPopParamsMCI,
  },
  [TEST_POPULATION_TYPES.TEST_S_MCI_PASSAGE_LONG]: {
    id: `${TEST_POPULATION_TYPES.TEST_S_MCI_PASSAGE_LONG}`,
    instructions: {
      text: 'This passage has instructions about something that you should read about here.',
    },
    populationData:
      multiChoiceImageQuestions[TEST_CHOICE_TYPES.TEST_S_MCI_LONG],
    hint: copyHints(hints[TEST_HINT_TYPES.ES_MULTIPLE_MEDIUM]),
    passage: texts[TEST_TEXTS_TYPES.LONG],
    image: images[TEST_IMAGE_TYPES.CAT_UNDER_TABLE],
    ...constSpanishPopParamsMCI,
  },
};

const constSpanishPopParamsMS = {
  templateID: TemplateID.MULTI_SELECT,
  complexity: 'Application' as TEIComplexity,
  associatedSuccessCriteria: ['multiselect'],
  domain: { text: 'Multiselect domain' },
  standard: { text: 'Multiselect standard' },
  learningTarget: [learningTargets[TEST_LEARNING_TARGET_TYPES.MULTI_SELECT]],
  feedbackFirstIncorrect: {
    content: 'Insert first incorrect feedback - Test.R.MS',
  },
  feedbackSecondIncorrect: {
    content: 'Insert second incorrect feedback - Test.R.MS',
  },
  ...constSpanishPopParams,
};

export const populationsSpanishMS: { [key: number]: Population } = {
  [TEST_POPULATION_TYPES.TEST_S_MS_SHORT]: {
    id: `${TEST_POPULATION_TYPES.TEST_S_MS_SHORT}`,
    populationData: multiSelectQuestions[TEST_CHOICE_TYPES.TEST_S_MS_SHORT],
    hint: copyHints(hints[TEST_HINT_TYPES.ES_SINGLE_SHORT]),
    ...constSpanishPopParamsMS,
  },
  [TEST_POPULATION_TYPES.TEST_S_MS_MEDIUM]: {
    id: `${TEST_POPULATION_TYPES.TEST_S_MS_MEDIUM}`,
    populationData: multiSelectQuestions[TEST_CHOICE_TYPES.TEST_S_MS_MEDIUM],
    hint: copyHints(hints[TEST_HINT_TYPES.ES_SINGLE_MEDIUM]),
    ...constSpanishPopParamsMS,
  },
  [TEST_POPULATION_TYPES.TEST_S_MS_LONG]: {
    id: `${TEST_POPULATION_TYPES.TEST_S_MS_LONG}`,
    instructions: {
      text: 'This passage has instructions about something that you should read about here.',
    },
    populationData: multiSelectQuestions[TEST_CHOICE_TYPES.TEST_S_MS_LONG],
    hint: copyHints(hints[TEST_HINT_TYPES.ES_SINGLE_LONG]),
    ...constSpanishPopParamsMS,
  },
  [TEST_POPULATION_TYPES.TEST_S_MS_PASSAGE_SHORT]: {
    id: `${TEST_POPULATION_TYPES.TEST_S_MS_PASSAGE_SHORT}`,
    populationData: multiSelectQuestions[TEST_CHOICE_TYPES.TEST_S_MS_SHORT],
    hint: copyHints(hints[TEST_HINT_TYPES.ES_DOUBLE_MEDIUM]),
    passage: texts[TEST_TEXTS_TYPES.SHORT],
    ...constSpanishPopParamsMS,
  },
  [TEST_POPULATION_TYPES.TEST_S_MS_PASSAGE_MEDIUM]: {
    id: `${TEST_POPULATION_TYPES.TEST_S_MS_PASSAGE_MEDIUM}`,
    populationData: multiSelectQuestions[TEST_CHOICE_TYPES.TEST_S_MS_MEDIUM],
    hint: copyHints(hints[TEST_HINT_TYPES.ES_MULTIPLE_MEDIUM]),
    passage: texts[TEST_TEXTS_TYPES.MEDIUM],
    ...constSpanishPopParamsMS,
  },
  [TEST_POPULATION_TYPES.TEST_S_MS_PASSAGE_LONG]: {
    id: `${TEST_POPULATION_TYPES.TEST_S_MS_PASSAGE_LONG}`,
    instructions: {
      text: 'This has instructions about something that you should read about here.',
    },
    populationData: multiSelectQuestions[TEST_CHOICE_TYPES.TEST_S_MS_LONG],
    hint: copyHints(hints[TEST_HINT_TYPES.ES_MULTIPLE_MEDIUM]),
    passage: texts[TEST_TEXTS_TYPES.LONG],
    image: images[TEST_IMAGE_TYPES.CAT_UNDER_TABLE],
    ...constSpanishPopParamsMS,
  },
};

const constSpanishPopParamsMSI = {
  templateID: TemplateID.MULTI_SELECT_IMAGE,
  complexity: 'Application' as TEIComplexity,
  associatedSuccessCriteria: ['multiselectimage'],
  domain: { text: 'Multiselect image domain' },
  standard: { text: 'Multiselect image standard' },
  learningTarget: [learningTargets[TEST_LEARNING_TARGET_TYPES.MULTI_SELECT]],
  feedbackFirstIncorrect: {
    content: 'Insert first incorrect feedback - Test.R.MSI',
  },
  feedbackSecondIncorrect: {
    content: 'Insert second incorrect feedback - Test.R.MSI',
  },
  ...constSpanishPopParams,
};

export const populationsSpanishMSI: { [key: number]: Population } = {
  [TEST_POPULATION_TYPES.TEST_S_MSI_SHORT]: {
    id: `${TEST_POPULATION_TYPES.TEST_S_MSI_SHORT}`,
    populationData:
      multiSelectImageQuestions[TEST_CHOICE_TYPES.TEST_S_MSI_SHORT],
    hint: copyHints(hints[TEST_HINT_TYPES.ES_SINGLE_SHORT]),
    ...constSpanishPopParamsMSI,
  },
  [TEST_POPULATION_TYPES.TEST_S_MSI_MEDIUM]: {
    id: `${TEST_POPULATION_TYPES.TEST_S_MSI_MEDIUM}`,
    populationData:
      multiSelectImageQuestions[TEST_CHOICE_TYPES.TEST_S_MSI_MEDIUM],
    hint: copyHints(hints[TEST_HINT_TYPES.ES_SINGLE_MEDIUM]),
    ...constSpanishPopParamsMSI,
  },
  [TEST_POPULATION_TYPES.TEST_S_MSI_LONG]: {
    id: `${TEST_POPULATION_TYPES.TEST_S_MSI_LONG}`,
    populationData:
      multiSelectImageQuestions[TEST_CHOICE_TYPES.TEST_S_MSI_LONG],
    hint: copyHints(hints[TEST_HINT_TYPES.ES_SINGLE_LONG]),
    ...constSpanishPopParamsMSI,
  },
  [TEST_POPULATION_TYPES.TEST_S_MSI_PASSAGE_SHORT]: {
    id: `${TEST_POPULATION_TYPES.TEST_S_MSI_PASSAGE_SHORT}`,
    populationData:
      multiSelectImageQuestions[TEST_CHOICE_TYPES.TEST_S_MSI_SHORT],
    hint: copyHints(hints[TEST_HINT_TYPES.ES_DOUBLE_MEDIUM]),
    passage: texts[TEST_TEXTS_TYPES.SHORT],
    ...constSpanishPopParamsMSI,
  },
  [TEST_POPULATION_TYPES.TEST_S_MSI_PASSAGE_MEDIUM]: {
    id: `${TEST_POPULATION_TYPES.TEST_S_MSI_PASSAGE_MEDIUM}`,
    populationData:
      multiSelectImageQuestions[TEST_CHOICE_TYPES.TEST_S_MSI_MEDIUM],
    hint: copyHints(hints[TEST_HINT_TYPES.ES_MULTIPLE_MEDIUM]),
    passage: texts[TEST_TEXTS_TYPES.MEDIUM],
    ...constSpanishPopParamsMSI,
  },
  [TEST_POPULATION_TYPES.TEST_S_MSI_PASSAGE_LONG]: {
    id: `${TEST_POPULATION_TYPES.TEST_S_MSI_PASSAGE_LONG}`,
    populationData:
      multiSelectImageQuestions[TEST_CHOICE_TYPES.TEST_S_MSI_LONG],
    hint: copyHints(hints[TEST_HINT_TYPES.ES_MULTIPLE_MEDIUM]),
    passage: texts[TEST_TEXTS_TYPES.LONG],
    instructions: {
      text: 'This has instructions about something that you should read about here.',
    },
    image: images[TEST_IMAGE_TYPES.CAT_UNDER_TABLE],
    ...constSpanishPopParamsMSI,
  },
};

const constSpanishPopParamsIC = {
  templateID: TemplateID.INLINE_CHOICE,
  complexity: 'Application' as TEIComplexity,
  associatedSuccessCriteria: ['inlinechoice'],
  domain: { text: 'Inline choice domain' },
  standard: { text: 'Inline choice standard' },
  learningTarget: [learningTargets[TEST_LEARNING_TARGET_TYPES.INLINE_CHOICE]],
  feedbackFirstIncorrect: {
    content: 'Insert first incorrect feedback - Test.R.IC',
  },
  feedbackSecondIncorrect: {
    content: 'Insert second incorrect feedback - Test.R.IC',
  },
  ...constSpanishPopParams,
};

export const populationsSpanishIC: { [key: number]: Population } = {
  [TEST_POPULATION_TYPES.TEST_S_IC_SHORT]: {
    id: `${TEST_POPULATION_TYPES.TEST_S_IC_SHORT}`,
    hint: copyHints(hints[TEST_HINT_TYPES.ES_SINGLE_SHORT]),
    populationData: inlineChoiceQuestions[TEST_CHOICE_TYPES.TEST_S_IC_SHORT],
    ...constSpanishPopParamsIC,
  },
  [TEST_POPULATION_TYPES.TEST_S_IC_MEDIUM]: {
    id: `${TEST_POPULATION_TYPES.TEST_S_IC_MEDIUM}`,
    hint: copyHints(hints[TEST_HINT_TYPES.ES_SINGLE_MEDIUM]),
    populationData: inlineChoiceQuestions[TEST_CHOICE_TYPES.TEST_S_IC_MEDIUM],
    ...constSpanishPopParamsIC,
  },
  [TEST_POPULATION_TYPES.TEST_S_IC_LONG]: {
    id: `${TEST_POPULATION_TYPES.TEST_S_IC_LONG}`,
    hint: copyHints(hints[TEST_HINT_TYPES.ES_SINGLE_LONG]),
    instructions: {
      text: 'This has instructions about something that you should read about here.',
    },
    populationData: inlineChoiceQuestions[TEST_CHOICE_TYPES.TEST_S_IC_LONG],
    image: images[TEST_IMAGE_TYPES.CAT_UNDER_TABLE],
    ...constSpanishPopParamsIC,
  },
};

const constSpanishPopParamsTE = {
  templateID: TemplateID.TEXT_ENTRY,
  complexity: 'Application' as TEIComplexity,
  associatedSuccessCriteria: ['textentry'],
  domain: { text: 'Text entry domain' },
  standard: { text: 'Text entry standard' },
  learningTarget: [learningTargets[TEST_LEARNING_TARGET_TYPES.TEXT_ENTRY]],
  feedbackFirstIncorrect: {
    content: 'Insert first incorrect feedback - Test.R.TE',
  },
  feedbackSecondIncorrect: {
    content: 'Insert second incorrect feedback - Test.R.TE',
  },
  ...constSpanishPopParams,
};

export const populationsSpanishTE: { [key: number]: Population } = {
  [TEST_POPULATION_TYPES.TEST_S_TE_SHORT]: {
    id: `${TEST_POPULATION_TYPES.TEST_S_TE_SHORT}`,
    hint: copyHints(hints[TEST_HINT_TYPES.ES_SINGLE_SHORT]),
    populationData: textEntryQuestions[TEST_CHOICE_TYPES.TEST_S_TE_SHORT],
    ...constSpanishPopParamsTE,
  },
  [TEST_POPULATION_TYPES.TEST_S_TE_MEDIUM]: {
    id: `${TEST_POPULATION_TYPES.TEST_S_TE_MEDIUM}`,
    hint: copyHints(hints[TEST_HINT_TYPES.ES_SINGLE_MEDIUM]),
    populationData: textEntryQuestions[TEST_CHOICE_TYPES.TEST_S_TE_MEDIUM],
    ...constSpanishPopParamsTE,
  },
  [TEST_POPULATION_TYPES.TEST_S_TE_LONG]: {
    id: `${TEST_POPULATION_TYPES.TEST_S_TE_LONG}`,
    hint: copyHints(hints[TEST_HINT_TYPES.ES_SINGLE_LONG]),
    instructions: {
      text: 'This has instructions about something that you should read about here.',
    },
    populationData: textEntryQuestions[TEST_CHOICE_TYPES.TEST_S_TE_LONG],
    image: images[TEST_IMAGE_TYPES.CAT_UNDER_TABLE],
    ...constSpanishPopParamsTE,
  },
};

const constSpanishPopParamsSCR = {
  templateID: TemplateID.CONSTRUCTED_RESPONSE,
  complexity: 'Application' as TEIComplexity,
  associatedSuccessCriteria: [
    TestAssociatedSuccessCriteria.SHORT_CONST_RESPONSE,
  ],
  domain: { text: 'Dominio de respuesta corta construida' },
  standard: { text: 'Estándar de respuesta corta construida' },
  learningTarget: [
    learningTargets[TEST_LEARNING_TARGET_TYPES.SHORT_CONSTRUCTED_RESPONSE],
  ],
  ...constSpanishPopParams,
};

const constSpanishPopParamsECR = {
  templateID: TemplateID.EXTENDED_CONSTRUCTED_RESPONSE,
  complexity: 'Application' as TEIComplexity,
  associatedSuccessCriteria: [
    TestAssociatedSuccessCriteria.EXTENDED_CONST_RESPONSE,
  ],
  domain: { text: 'Dominio de respuesta construida extendida' },
  standard: { text: 'Estándar de respuesta construida extendida' },
  learningTarget: [
    learningTargets[TEST_LEARNING_TARGET_TYPES.EXTENDED_CONSTRUCTED_RESPONSE],
  ],
  ...constSpanishPopParams,
};

const constSpanishPopParamsSCRR = {
  templateID: TemplateID.CONSTRUCTED_RESPONSE_WITH_RUBRIC,
  complexity: 'Application' as TEIComplexity,
  associatedSuccessCriteria: [
    TestAssociatedSuccessCriteria.SHORT_CONST_RESPONSE_RUBRIC,
  ],
  domain: { text: 'Dominio de la rúbrica de respuesta corta construida' },
  standard: { text: 'Estándar de rúbrica de respuesta corta construida' },
  learningTarget: [
    learningTargets[
      TEST_LEARNING_TARGET_TYPES.SHORT_CONSTRUCTED_RESPONSE_WITH_RUBRIC
    ],
  ],
  ...constSpanishPopParams,
};

const constSpanishPopParamsECRR = {
  templateID: TemplateID.EXTENDED_CONSTRUCTED_RESPONSE_WITH_RUBRIC,
  complexity: 'Application' as TEIComplexity,
  associatedSuccessCriteria: [
    TestAssociatedSuccessCriteria.EXTENDED_CONST_RESPONSE_RUBRIC,
  ],
  domain: { text: 'Dominio de la rúbrica de respuesta construida extendida' },
  standard: { text: 'Estándar de rúbrica de respuesta construida extendida' },
  learningTarget: [
    learningTargets[
      TEST_LEARNING_TARGET_TYPES.EXTENDED_CONSTRUCTED_RESPONSE_WITH_RUBRIC
    ],
  ],
  ...constSpanishPopParams,
};

export const populationsSpanishSCR: { [key: number]: Population } = {
  [TEST_POPULATION_TYPES.TEST_S_SCR_SHORT]: {
    id: `${TEST_POPULATION_TYPES.TEST_S_SCR_SHORT}`,
    hint: copyHints(hints[TEST_HINT_TYPES.ES_SINGLE_SHORT]),
    populationData:
      constructedResponseQuestions[TEST_CHOICE_TYPES.TEST_S_SCR_SHORT],
    ...constSpanishPopParamsSCR,
  },
  [TEST_POPULATION_TYPES.TEST_S_SCR_MEDIUM]: {
    id: `${TEST_POPULATION_TYPES.TEST_S_SCR_MEDIUM}`,
    hint: copyHints(hints[TEST_HINT_TYPES.ES_SINGLE_MEDIUM]),
    populationData:
      constructedResponseQuestions[TEST_CHOICE_TYPES.TEST_S_SCR_MEDIUM],
    ...constSpanishPopParamsSCR,
  },
  [TEST_POPULATION_TYPES.TEST_S_SCR_LONG]: {
    id: `${TEST_POPULATION_TYPES.TEST_S_SCR_LONG}`,
    hint: copyHints(hints[TEST_HINT_TYPES.ES_SINGLE_LONG]),
    instructions: {
      text: 'Esto tiene instrucciones sobre algo que deberías leer aquí.',
    },
    populationData:
      constructedResponseQuestions[TEST_CHOICE_TYPES.TEST_S_SCR_LONG],
    ...constSpanishPopParamsSCR,
  },
  [TEST_POPULATION_TYPES.TEST_S_SCR_PASSAGE_SHORT]: {
    id: `${TEST_POPULATION_TYPES.TEST_S_SCR_PASSAGE_SHORT}`,
    hint: copyHints(hints[TEST_HINT_TYPES.ES_SINGLE_SHORT]),
    populationData:
      constructedResponseQuestions[TEST_CHOICE_TYPES.TEST_S_SCR_SHORT],
    passage: texts[TEST_TEXTS_TYPES.SHORT],
    ...constSpanishPopParamsSCR,
  },
  [TEST_POPULATION_TYPES.TEST_S_SCR_PASSAGE_MEDIUM]: {
    id: `${TEST_POPULATION_TYPES.TEST_S_SCR_PASSAGE_MEDIUM}`,
    hint: copyHints(hints[TEST_HINT_TYPES.ES_SINGLE_MEDIUM]),
    populationData:
      constructedResponseQuestions[TEST_CHOICE_TYPES.TEST_S_SCR_MEDIUM],
    passage: texts[TEST_TEXTS_TYPES.MEDIUM],
    ...constSpanishPopParamsSCR,
  },
  [TEST_POPULATION_TYPES.TEST_S_SCR_PASSAGE_LONG]: {
    id: `${TEST_POPULATION_TYPES.TEST_S_SCR_PASSAGE_LONG}`,
    hint: copyHints(hints[TEST_HINT_TYPES.ES_SINGLE_LONG]),
    instructions: {
      text: 'Esto tiene instrucciones sobre algo que deberías leer aquí.',
    },
    populationData:
      constructedResponseQuestions[TEST_CHOICE_TYPES.TEST_S_SCRR_LONG],
    passage: texts[TEST_TEXTS_TYPES.LONG],
    ...constSpanishPopParamsSCR,
  },
  [TEST_POPULATION_TYPES.TEST_S_SCRR_SHORT]: {
    id: `${TEST_POPULATION_TYPES.TEST_S_SCRR_SHORT}`,
    hint: copyHints(hints[TEST_HINT_TYPES.ES_SINGLE_SHORT]),
    populationData:
      constructedResponseQuestions[TEST_CHOICE_TYPES.TEST_S_SCRR_SHORT],
    ...constSpanishPopParamsSCRR,
  },
  [TEST_POPULATION_TYPES.TEST_S_SCRR_MEDIUM]: {
    id: `${TEST_POPULATION_TYPES.TEST_S_SCRR_MEDIUM}`,
    hint: copyHints(hints[TEST_HINT_TYPES.ES_SINGLE_MEDIUM]),
    populationData:
      constructedResponseQuestions[TEST_CHOICE_TYPES.TEST_S_SCRR_MEDIUM],
    ...constSpanishPopParamsSCRR,
  },
  [TEST_POPULATION_TYPES.TEST_S_SCRR_LONG]: {
    id: `${TEST_POPULATION_TYPES.TEST_S_SCRR_LONG}`,
    hint: copyHints(hints[TEST_HINT_TYPES.ES_SINGLE_LONG]),
    instructions: {
      text: 'Esto tiene instrucciones sobre algo que deberías leer aquí.',
    },
    populationData:
      constructedResponseQuestions[TEST_CHOICE_TYPES.TEST_S_SCRR_LONG],
    ...constSpanishPopParamsSCRR,
  },
  [TEST_POPULATION_TYPES.TEST_S_SCRR_PASSAGE_SHORT]: {
    id: `${TEST_POPULATION_TYPES.TEST_S_SCRR_PASSAGE_SHORT}`,
    hint: copyHints(hints[TEST_HINT_TYPES.ES_SINGLE_SHORT]),
    populationData:
      constructedResponseQuestions[TEST_CHOICE_TYPES.TEST_S_SCRR_SHORT],
    passage: texts[TEST_TEXTS_TYPES.SHORT],
    ...constSpanishPopParamsSCRR,
  },
  [TEST_POPULATION_TYPES.TEST_S_SCRR_PASSAGE_MEDIUM]: {
    id: `${TEST_POPULATION_TYPES.TEST_S_SCRR_PASSAGE_MEDIUM}`,
    hint: copyHints(hints[TEST_HINT_TYPES.ES_SINGLE_MEDIUM]),
    populationData:
      constructedResponseQuestions[TEST_CHOICE_TYPES.TEST_S_SCRR_MEDIUM],
    passage: texts[TEST_TEXTS_TYPES.MEDIUM],
    ...constSpanishPopParamsSCRR,
  },
  [TEST_POPULATION_TYPES.TEST_S_SCRR_PASSAGE_LONG]: {
    id: `${TEST_POPULATION_TYPES.TEST_S_SCRR_PASSAGE_LONG}`,
    hint: copyHints(hints[TEST_HINT_TYPES.ES_SINGLE_LONG]),
    instructions: {
      text: 'Esto tiene instrucciones sobre algo que deberías leer aquí.',
    },
    populationData:
      constructedResponseQuestions[TEST_CHOICE_TYPES.TEST_S_SCRR_LONG],
    passage: texts[TEST_TEXTS_TYPES.LONG],
    image: images[TEST_IMAGE_TYPES.CAT_UNDER_TABLE],
    ...constSpanishPopParamsSCRR,
  },
};

export const populationsSpanishECR: { [key: number]: Population } = {
  [TEST_POPULATION_TYPES.TEST_S_ECR_SHORT]: {
    id: `${TEST_POPULATION_TYPES.TEST_S_ECR_SHORT}`,
    hint: copyHints(chartHints[TEST_HINT_TYPES.ES_SINGLE_SHORT]),
    populationData:
      extendedConstructedResponseQuestions[TEST_CHOICE_TYPES.TEST_S_ECR_SHORT],
    ...constSpanishPopParamsECR,
  },
  [TEST_POPULATION_TYPES.TEST_S_ECR_MEDIUM]: {
    id: `${TEST_POPULATION_TYPES.TEST_S_ECR_MEDIUM}`,
    hint: copyHints(chartHints[TEST_HINT_TYPES.ES_SINGLE_MEDIUM]),
    populationData:
      extendedConstructedResponseQuestions[TEST_CHOICE_TYPES.TEST_S_ECR_MEDIUM],
    ...constSpanishPopParamsECR,
  },
  [TEST_POPULATION_TYPES.TEST_S_ECR_LONG]: {
    id: `${TEST_POPULATION_TYPES.TEST_S_ECR_LONG}`,
    hint: copyHints(hints[TEST_HINT_TYPES.ES_SINGLE_LONG]),
    instructions: {
      text: 'Esto tiene instrucciones sobre algo que deberías leer aquí.',
    },
    populationData:
      extendedConstructedResponseQuestions[TEST_CHOICE_TYPES.TEST_S_ECR_LONG],
    ...constSpanishPopParamsECR,
  },
  [TEST_POPULATION_TYPES.TEST_S_ECR_PASSAGE_SHORT]: {
    id: `${TEST_POPULATION_TYPES.TEST_S_ECR_PASSAGE_SHORT}`,
    hint: copyHints(hints[TEST_HINT_TYPES.ES_SINGLE_SHORT]),
    populationData:
      extendedConstructedResponseQuestions[TEST_CHOICE_TYPES.TEST_S_ECR_SHORT],
    passage: texts[TEST_TEXTS_TYPES.SHORT],
    ...constSpanishPopParamsECR,
  },
  [TEST_POPULATION_TYPES.TEST_S_ECR_PASSAGE_MEDIUM]: {
    id: `${TEST_POPULATION_TYPES.TEST_S_ECR_PASSAGE_MEDIUM}`,
    hint: copyHints(hints[TEST_HINT_TYPES.ES_SINGLE_MEDIUM]),
    populationData:
      extendedConstructedResponseQuestions[TEST_CHOICE_TYPES.TEST_S_ECR_MEDIUM],
    passage: texts[TEST_TEXTS_TYPES.MEDIUM],
    ...constSpanishPopParamsECR,
  },
  [TEST_POPULATION_TYPES.TEST_S_ECR_PASSAGE_LONG]: {
    id: `${TEST_POPULATION_TYPES.TEST_S_ECR_PASSAGE_LONG}`,
    hint: copyHints(hints[TEST_HINT_TYPES.ES_SINGLE_LONG]),
    instructions: {
      text: 'Esto tiene instrucciones sobre algo que deberías leer aquí.',
    },
    populationData:
      extendedConstructedResponseQuestions[TEST_CHOICE_TYPES.TEST_S_ECRR_LONG],
    passage: texts[TEST_TEXTS_TYPES.LONG],
    ...constSpanishPopParamsECR,
  },
  [TEST_POPULATION_TYPES.TEST_S_ECRR_SHORT]: {
    id: `${TEST_POPULATION_TYPES.TEST_S_ECRR_SHORT}`,
    hint: copyHints(chartHints[TEST_HINT_TYPES.ES_SINGLE_SHORT]),
    populationData:
      extendedConstructedResponseQuestions[TEST_CHOICE_TYPES.TEST_S_ECRR_SHORT],
    ...constSpanishPopParamsECRR,
  },
  [TEST_POPULATION_TYPES.TEST_S_ECRR_MEDIUM]: {
    id: `${TEST_POPULATION_TYPES.TEST_S_ECRR_MEDIUM}`,
    hint: copyHints(chartHints[TEST_HINT_TYPES.ES_SINGLE_MEDIUM]),
    populationData:
      extendedConstructedResponseQuestions[
        TEST_CHOICE_TYPES.TEST_S_ECRR_MEDIUM
      ],
    ...constSpanishPopParamsECRR,
  },
  [TEST_POPULATION_TYPES.TEST_S_ECRR_LONG]: {
    id: `${TEST_POPULATION_TYPES.TEST_S_ECRR_LONG}`,
    hint: copyHints(hints[TEST_HINT_TYPES.ES_SINGLE_LONG]),
    instructions: {
      text: 'Esto tiene instrucciones sobre algo que deberías leer aquí.',
    },
    populationData:
      extendedConstructedResponseQuestions[TEST_CHOICE_TYPES.TEST_S_ECRR_LONG],
    ...constSpanishPopParamsECRR,
  },
  [TEST_POPULATION_TYPES.TEST_S_ECRR_PASSAGE_SHORT]: {
    id: `${TEST_POPULATION_TYPES.TEST_S_ECRR_PASSAGE_SHORT}`,
    hint: copyHints(hints[TEST_HINT_TYPES.ES_SINGLE_SHORT]),
    populationData:
      extendedConstructedResponseQuestions[TEST_CHOICE_TYPES.TEST_S_ECRR_SHORT],
    passage: texts[TEST_TEXTS_TYPES.SHORT],
    ...constSpanishPopParamsECRR,
  },
  [TEST_POPULATION_TYPES.TEST_S_ECRR_PASSAGE_MEDIUM]: {
    id: `${TEST_POPULATION_TYPES.TEST_S_ECRR_PASSAGE_MEDIUM}`,
    hint: copyHints(hints[TEST_HINT_TYPES.ES_SINGLE_MEDIUM]),
    populationData:
      extendedConstructedResponseQuestions[
        TEST_CHOICE_TYPES.TEST_S_ECRR_MEDIUM
      ],
    passage: texts[TEST_TEXTS_TYPES.MEDIUM],
    ...constSpanishPopParamsECRR,
  },
  [TEST_POPULATION_TYPES.TEST_S_ECRR_PASSAGE_LONG]: {
    id: `${TEST_POPULATION_TYPES.TEST_S_ECRR_PASSAGE_LONG}`,
    hint: copyHints(hints[TEST_HINT_TYPES.ES_SINGLE_LONG]),
    instructions: {
      text: 'Esto tiene instrucciones sobre algo que deberías leer aquí.',
    },
    populationData:
      extendedConstructedResponseQuestions[TEST_CHOICE_TYPES.TEST_S_ECRR_LONG],
    passage: texts[TEST_TEXTS_TYPES.LONG],
    image: images[TEST_IMAGE_TYPES.CAT_UNDER_TABLE],
    ...constSpanishPopParamsECRR,
  },
};
